// ----------------------------------------------------------------------------
// Product Form Extension: Loyalty Pricing (overlay)
// ----------------------------------------------------------------------------
// Signed-in loyalty pricing on the PDP, implemented as an OVERLAY that never
// shares a DOM node with the price the Avis options app manages.
//
// Why: Avis hijacks the theme's own price node (#ProductPrice / [data-product-
// price]) and rewrites its text on a setInterval to the plain, non-loyalty
// base+add-ons total. Racing it (reacting to its writes and rewriting the same
// node) can't be won. So instead:
//
//   - Avis keeps owning #ProductPrice — the NON-LOYALTY total (base + add-ons).
//     product-price.js / product-form--property-pricing.js render it too, to the
//     SAME non-loyalty value, so nobody fights. That node is our READ source.
//   - We render the loyalty price into our OWN node ([data-loyalty-price-current])
//     that Avis has no knowledge of, layering the loyalty discount on top of
//     whatever non-loyalty total the sink currently shows. When loyalty pricing is
//     active the sink's price row is hidden and the overlay is shown instead.
//   - A MutationObserver on the sink recomputes the overlay whenever the non-
//     loyalty total changes (Avis add-ons, variant change, theme surcharges). We
//     only ever write the overlay, never the sink, so there is no loop and no
//     give-up/rate heuristic.
//
// getCurrentDisplayPrice() is pointed at the overlay first (see product-form.js
// DISPLAY_PRICE_SELECTORS), so the add-to-cart CTA reflects the loyalty price.
//
// The matching + rounding MIRROR the Discount Function (lethal-performance-app →
// extensions/loyalty-pricing/src/match-rules.ts); loyaltyPriceCents floors the
// discount to match checkout to the cent. Cards compute the same result
// server-side (snippets/loyalty-price-percentage.liquid).
// ----------------------------------------------------------------------------
// DOM contract (rendered server-side in product-price/product-form__price.liquid):
//   [data-loyalty-price-overlay]        wrapper, shown only when loyalty-eligible
//     [data-loyalty-price-compare]        struck non-loyalty total
//     [data-loyalty-price-current]        the loyalty price (read by the CTA)
//   The sink's price row is [data-loyalty-price-sink] (its .product-price__pricing).
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import { formatMoney } from '@shopify/theme-currency';
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import { loyaltyFactorFor, loyaltyPriceCents } from 'scripts/helpers/loyalty-pricing';

// -------------------------------------
// Functionality
// -------------------------------------

var moneyFormat = () => window.theme && window.theme.settingCurrencyFormat || '{{amount}}';

/**
 * Read the current NON-LOYALTY total (cents) from the sink's price element.
 * Prefers the live text (Avis-authored, includes add-ons); falls back to the
 * data-price-cents our own renderers stamp.
 */
function readNonLoyaltyCents(iamForm, sinkPrice) {
  var _iamForm$parseDisplay;
  if (!sinkPrice) return null;
  var parsed = (_iamForm$parseDisplay = iamForm.parseDisplayedMoney) === null || _iamForm$parseDisplay === void 0 ? void 0 : _iamForm$parseDisplay.call(iamForm, sinkPrice.textContent);
  if (Number.isFinite(parsed)) return parsed;
  var cents = Number.parseInt(sinkPrice.dataset.priceCents, 10);
  return Number.isFinite(cents) ? cents : null;
}

/**
 * (Re)render the loyalty overlay for a product form from the sink's current
 * non-loyalty total. Writes only the overlay — never the sink.
 * @param {Object} iamForm
 */
function renderLoyaltyOverlay(iamForm) {
  var _iamForm$querySelecto;
  var host = iamForm === null || iamForm === void 0 || (_iamForm$querySelecto = iamForm.querySelector) === null || _iamForm$querySelecto === void 0 ? void 0 : _iamForm$querySelecto.call(iamForm, 'iam-product-price');
  if (!host) return;
  var overlay = host.querySelector('[data-loyalty-price-overlay]');
  if (!overlay) return; // product wasn't rendered loyalty-eligible

  var currentEl = overlay.querySelector('[data-loyalty-price-current]');
  var compareEl = overlay.querySelector('[data-loyalty-price-compare]');
  var sinkPrice = host.querySelector('[data-product-price]');
  var sinkRow = host.querySelector('[data-loyalty-price-sink]') || (sinkPrice === null || sinkPrice === void 0 ? void 0 : sinkPrice.closest('.product-price__pricing'));
  var excluded = host.dataset.loyaltyExcluded === '1';
  var pct = excluded ? 0 : loyaltyFactorFor(host.dataset.productVendor);
  var nonLoyalty = readNonLoyaltyCents(iamForm, sinkPrice);
  var active = pct > 0 && Number.isFinite(nonLoyalty);

  // Toggle which price is visible. When inactive, clear the overlay's value so
  // getCurrentDisplayPrice falls through to the sink (the non-loyalty price).
  overlay.hidden = !active;
  if (sinkRow) sinkRow.hidden = active;
  if (!active) {
    if (currentEl) currentEl.textContent = '';
    return;
  }
  var loyalty = loyaltyPriceCents(nonLoyalty, pct);
  if (currentEl) {
    currentEl.innerHTML = formatMoney(loyalty, moneyFormat());
    currentEl.dataset.priceCents = loyalty;
  }
  if (compareEl) {
    compareEl.innerHTML = formatMoney(nonLoyalty, moneyFormat());
    compareEl.dataset.priceCents = nonLoyalty;
    compareEl.hidden = nonLoyalty <= loyalty;
  }
}

// -------------------------------------
// Initialization
// -------------------------------------

/**
 * Wire one product form: initial render + a MutationObserver on the SINK so any
 * later change to the non-loyalty total (Avis's setInterval, variant change,
 * theme surcharges) re-renders the overlay. The observer watches the sink only,
 * and we only write the overlay, so our writes can't retrigger it — no loop.
 */
function initLoyaltyOverlay(iamForm) {
  var _iamForm$querySelecto2;
  var host = iamForm === null || iamForm === void 0 || (_iamForm$querySelecto2 = iamForm.querySelector) === null || _iamForm$querySelecto2 === void 0 ? void 0 : _iamForm$querySelecto2.call(iamForm, 'iam-product-price');
  if (!host || !host.querySelector('[data-loyalty-price-overlay]')) return;
  if (host.dataset.loyaltyOverlayReady === '1') return;
  host.dataset.loyaltyOverlayReady = '1';
  renderLoyaltyOverlay(iamForm);
  var sink = host.querySelector('[data-product-pricing]') || host.querySelector('[data-product-price]');
  if (sink) {
    var observer = new MutationObserver(() => renderLoyaltyOverlay(iamForm));
    observer.observe(sink, {
      characterData: true,
      childList: true,
      subtree: true
    });
  }
}

// `productForm:init` is atomic, so this runs even if the form initialized before
// this module subscribed.
iamotaEventHandler.subscribe('productForm:init', _ref => {
  var {
    iamForm
  } = _ref;
  initLoyaltyOverlay(iamForm);
}, {
  source: "product-form--loyalty-price.js"
});

// Variant changes can change the base price, so re-render explicitly too.
// (Loyalty eligibility is vendor-based and product-level, so it can't flip.)
iamotaEventHandler.subscribe('productForm:variant:changed', _ref2 => {
  var {
    iamProductForm
  } = _ref2;
  renderLoyaltyOverlay(iamProductForm);
}, {
  source: "product-form--loyalty-price.js"
});